import { useState, useEffect, useCallback } from "react";
import { KeyRound, Clock, Loader2, ArrowRight, MessageCircle, ExternalLink, CheckCircle2, Users, UserCheck, AlertTriangle, CalendarDays, Trophy, GraduationCap, Building2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, formatWIB } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { PasswordInput, Field, btnPrimary } from "@/components/AuthShell";
import { PIPELINE_STATUSES, PIPELINE_STATUS_LABELS, PIPELINE_STATUS_CLS, FU_STATUS_LABELS, waLink } from "@/lib/prospectMeta";

function ChangePasswordDialog({ onClose }) {
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (newPw !== confirm) return setError("Konfirmasi tidak cocok.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { old_password: oldPw, new_password: newPw });
      toast.success("Kata sandi berhasil diperbarui.");
      onClose();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="change-password-dialog">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Ganti Kata Sandi</h3>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Kata Sandi Saat Ini"><PasswordInput required value={oldPw} onChange={(e) => setOldPw(e.target.value)} testId="dashboard-old-password-input" /></Field>
          <Field label="Kata Sandi Baru"><PasswordInput required value={newPw} onChange={(e) => setNewPw(e.target.value)} testId="dashboard-new-password-input" /></Field>
          <Field label="Konfirmasi Kata Sandi Baru"><PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} testId="dashboard-confirm-password-input" /></Field>
          {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2.5 rounded-lg" data-testid="dashboard-change-cancel">Batal</button>
            <button type="submit" disabled={loading} className={btnPrimary} data-testid="dashboard-change-submit">{loading && <Loader2 className="h-4 w-4 animate-spin" />} Simpan</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tone, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`text-left bg-white border rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow ${tone.border}`}>
      <div className={`inline-flex items-center justify-center h-8 w-8 rounded-lg mb-2 ${tone.bg} ${tone.text}`}><Icon className="h-4 w-4" /></div>
      <p className="text-2xl font-bold text-slate-900" data-testid={`${testid}-value`}>{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </button>
  );
}

const bucketMeta = {
  terlambat: { badge: "bg-red-50 text-red-700 border-red-200", label: "Terlambat" },
  hari_ini: { badge: "bg-orange-50 text-orange-700 border-orange-200", label: "Hari Ini" },
  mendatang: { badge: "bg-blue-50 text-blue-700 border-blue-200", label: "7 Hari Mendatang" },
};

const activityLabels = {
  prospek_baru: "Prospek baru ditambahkan",
  status: "Status pipeline berubah",
  followup: "Follow-up dibuat",
  followup_done: "Follow-up diselesaikan",
  konversi: "Prospek dikonversi",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("terlambat");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/dashboard"); setData(data); }
    catch { toast.error("Gagal memuat dashboard."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (fid) => {
    try { await api.patch(`/followups/${fid}/complete`); toast.success("Follow-up selesai."); load(); }
    catch { toast.error("Gagal memperbarui."); }
  };

  const s = data?.summary || {};
  const bucketItems = data?.followups?.[tab] || [];

  return (
    <AppLayout>
      <div data-testid="dashboard-welcome-banner" className="rounded-2xl bg-gradient-to-br from-[#0f2847] via-[#17416f] to-[#0a192f] text-white p-6 sm:p-8 relative overflow-hidden mb-6">
        <div className="absolute -bottom-16 -right-10 w-56 h-56 rounded-full bg-[#f97316]/20 blur-3xl" />
        <div className="relative z-10 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Selamat Datang, {user?.name?.split(" ").slice(-2).join(" ")}</h1>
            <p className="mt-2 text-blue-200 text-sm max-w-2xl">Ringkasan operasional cabang IEC Mustikajaya.</p>
            <div className="mt-4 inline-flex items-center gap-2 text-xs bg-white/10 border border-white/20 px-3 py-1.5 rounded-full">
              <Clock className="h-3 w-3" /> Login Terakhir: <span data-testid="header-last-login-info" className="font-medium">{formatWIB(user?.last_login_at)}</span>
            </div>
          </div>
          <button onClick={() => setShowDialog(true)} className="inline-flex items-center gap-2 text-sm font-medium bg-white/10 hover:bg-white/20 border border-white/20 px-3 py-2 rounded-lg" data-testid="dashboard-change-password-button"><KeyRound className="h-4 w-4" /> Ganti Kata Sandi</button>
        </div>
      </div>

      {loading || !data ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6" data-testid="dashboard-summary">
            <StatCard testid="stat-total-prospek" icon={Users} label="Total Prospek" value={s.total_prospek} tone={{ border: "border-slate-200", bg: "bg-slate-100", text: "text-slate-700" }} onClick={() => navigate("/prospek")} />
            <StatCard testid="stat-prospek-aktif" icon={UserCheck} label="Prospek Aktif" value={s.prospek_aktif} tone={{ border: "border-slate-200", bg: "bg-blue-50", text: "text-blue-700" }} onClick={() => navigate("/pipeline")} />
            <StatCard testid="stat-followup-terlambat" icon={AlertTriangle} label="Follow-up Terlambat" value={s.followup_terlambat} tone={{ border: "border-red-200", bg: "bg-red-50", text: "text-red-700" }} onClick={() => setTab("terlambat")} />
            <StatCard testid="stat-followup-hari-ini" icon={CalendarDays} label="Follow-up Hari Ini" value={s.followup_hari_ini} tone={{ border: "border-orange-200", bg: "bg-orange-50", text: "text-orange-700" }} onClick={() => setTab("hari_ini")} />
            <StatCard testid="stat-berhasil" icon={Trophy} label="Berhasil / Deal" value={s.berhasil} tone={{ border: "border-green-200", bg: "bg-green-50", text: "text-green-700" }} onClick={() => navigate("/pipeline?status=berhasil")} />
            <StatCard testid="stat-siswa-aktif" icon={GraduationCap} label="Siswa Aktif" value={s.siswa_aktif} tone={{ border: "border-slate-200", bg: "bg-indigo-50", text: "text-indigo-700" }} onClick={() => navigate("/siswa-klien")} />
            <StatCard testid="stat-klien-aktif" icon={Building2} label="Klien Aktif" value={s.klien_aktif} tone={{ border: "border-slate-200", bg: "bg-amber-50", text: "text-amber-700" }} onClick={() => navigate("/siswa-klien?tab=klien")} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5" data-testid="followup-panel">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-slate-900">Follow-up Perlu Tindakan</h2>
              </div>
              <div className="flex gap-1 mb-4 flex-wrap">
                {["terlambat", "hari_ini", "mendatang"].map((t) => (
                  <button key={t} onClick={() => setTab(t)} data-testid={`fu-tab-${t}`}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${tab === t ? bucketMeta[t].badge : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    {bucketMeta[t].label} ({(data.followups[t] || []).length})
                  </button>
                ))}
              </div>
              {bucketItems.length === 0 ? (
                <p className="text-sm text-slate-400 py-8 text-center" data-testid="followup-panel-empty">Tidak ada follow-up pada kategori ini.</p>
              ) : (
                <div className="space-y-2" data-testid="followup-panel-list">
                  {bucketItems.map((f) => (
                    <div key={f.id} className="border border-slate-100 rounded-lg p-3" data-testid={`fu-item-${f.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">{f.prospect_name}</p>
                          <p className="text-xs text-slate-500">{f.program || "-"} · {f.channel || "-"} · PIC {f.pic_name || "-"}</p>
                          <p className="text-xs mt-0.5"><span className={`px-1.5 py-0.5 rounded border text-[10px] ${bucketMeta[tab].badge}`}>{formatWIB(f.scheduled_at)}</span> <span className="text-slate-400">{FU_STATUS_LABELS[f.status] || f.status}</span></p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {f.prospect_phone && <a href={waLink(f.prospect_phone)} target="_blank" rel="noreferrer" data-testid={`fu-wa-${f.id}`} className="p-1.5 rounded hover:bg-green-100 text-green-600" title="WhatsApp"><MessageCircle className="h-4 w-4" /></a>}
                          <button onClick={() => navigate(`/prospek/${f.prospect_id}`)} data-testid={`fu-detail-${f.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-600" title="Buka Detail"><ExternalLink className="h-4 w-4" /></button>
                          <button onClick={() => complete(f.id)} data-testid={`fu-complete-${f.id}`} className="p-1.5 rounded hover:bg-green-100 text-green-600" title="Tandai Selesai"><CheckCircle2 className="h-4 w-4" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-5">
              <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="pipeline-breakdown">
                <h2 className="font-semibold text-slate-900 mb-3">Ringkasan Pipeline</h2>
                <div className="grid grid-cols-2 gap-2">
                  {PIPELINE_STATUSES.map((st) => (
                    <button key={st} onClick={() => navigate(`/pipeline?status=${st}`)} data-testid={`pipeline-stat-${st}`}
                      className={`text-left px-3 py-2 rounded-lg border text-xs ${PIPELINE_STATUS_CLS[st]}`}>
                      <span className="block text-lg font-bold">{data.pipeline[st] || 0}</span>
                      {PIPELINE_STATUS_LABELS[st]}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="recent-activity">
                <h2 className="font-semibold text-slate-900 mb-3">Aktivitas Terbaru</h2>
                {data.activities.length === 0 ? (
                  <p className="text-sm text-slate-400">Belum ada aktivitas.</p>
                ) : (
                  <ul className="space-y-2.5">
                    {data.activities.map((a, i) => (
                      <li key={i} className="text-xs border-b border-slate-50 pb-2 last:border-0" data-testid={`activity-${i}`}>
                        <span className="font-medium text-slate-700">{activityLabels[a.type] || a.type}</span>
                        <span className="text-slate-500"> — {a.name}</span>
                        <div className="text-[11px] text-slate-400 mt-0.5">{a.user_name || "-"} · {formatWIB(a.time)}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      {showDialog && <ChangePasswordDialog onClose={() => setShowDialog(false)} />}
    </AppLayout>
  );
}
